/**
 * SolArkSyncHistory - Displays Sol-Ark sync operation history
 *
 * Features:
 * - Collapsible section showing sync operation logs
 * - Color-coded status (success, failed, skipped)
 * - Shows sync type, items processed, duration
 * - Expandable error details for failures
 * - Load more pagination
 */

import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  SkipForward,
  RefreshCw,
  Zap,
  Plug,
  Sun,
  History,
  Database,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { authenticatedFetch } from '@/lib/api';
import { formatPreciseAgo } from '@/lib/relativeTime';
import { cn } from '@/lib/utils';

interface SyncOperation {
  id: string;
  sync_type: string;
  status: 'started' | 'success' | 'failed' | 'partial' | 'skipped';
  items_processed: number;
  items_success: number;
  items_failed: number;
  items_skipped: number;
  triggered_by: string;
  user_email: string | null;
  message: string | null;
  error_details: {
    name?: string;
    message?: string;
    stack?: string[];
  } | null;
  duration_ms: number | null;
  created_at: string;
}

interface SolArkSyncHistoryProps {
  /** Whether the section is initially expanded */
  defaultExpanded?: boolean;
  /** Number of items per page */
  pageSize?: number;
  /** Additional CSS classes */
  className?: string;
}

// Get icon for sync type
function getSyncTypeIcon(syncType: string) {
  const iconClass = 'w-3.5 h-3.5';
  switch (syncType) {
    case 'telemetry':
      return <Zap className={iconClass} />;
    case 'full':
      return <RefreshCw className={iconClass} />;
    case 'plants':
    case 'discover':
      return <Plug className={iconClass} />;
    case 'plants_telemetry':
      return <Sun className={iconClass} />;
    case 'historical':
      return <History className={iconClass} />;
    default:
      return <Database className={iconClass} />;
  }
}

// Get status color
function getStatusColor(status: string) {
  switch (status) {
    case 'success':
      return 'text-green-400 bg-green-500/20';
    case 'failed':
      return 'text-red-400 bg-red-500/20';
    case 'partial':
      return 'text-yellow-400 bg-yellow-500/20';
    case 'skipped':
      return 'text-muted-foreground bg-muted/50';
    case 'started':
      return 'text-blue-400 bg-blue-500/20';
    default:
      return 'text-muted-foreground bg-muted/50';
  }
}

// Get status icon
function getStatusIcon(status: string) {
  const iconClass = 'w-3.5 h-3.5';
  switch (status) {
    case 'success':
      return <CheckCircle className={iconClass} />;
    case 'failed':
      return <XCircle className={iconClass} />;
    case 'partial':
      return <AlertCircle className={iconClass} />;
    case 'skipped':
      return <SkipForward className={iconClass} />;
    default:
      return <Clock className={iconClass} />;
  }
}

// Format sync type for display
function formatSyncType(syncType: string) {
  const typeMap: Record<string, string> = {
    telemetry: 'Telemetry Sync',
    full: 'Full Sync',
    plants: 'Plant Discovery',
    discover: 'Plant Discovery (Retry)',
    plants_telemetry: 'Plant Telemetry',
    historical: 'Historical Sync',
    manual: 'Manual Sync',
  };
  return typeMap[syncType] || syncType;
}

// Format duration
function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// Single sync operation entry
function SyncOperationEntry({
  operation,
  isExpanded,
  onToggle,
}: {
  operation: SyncOperation;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const hasError = operation.status === 'failed' && operation.error_details;

  return (
    <div className="relative pl-6 pb-4 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-[9px] top-5 bottom-0 w-px bg-border last:hidden" />

      {/* Timeline dot with status icon */}
      <div
        className={cn(
          'absolute left-0 top-1 w-[18px] h-[18px] rounded-full flex items-center justify-center',
          getStatusColor(operation.status)
        )}
      >
        {getStatusIcon(operation.status)}
      </div>

      {/* Content */}
      <div className="min-w-0">
        <div
          className={cn(
            'flex items-start gap-2',
            hasError && 'cursor-pointer group hover:bg-muted/50 -mx-2 px-2 py-1 rounded-md'
          )}
          onClick={hasError ? onToggle : undefined}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                {getSyncTypeIcon(operation.sync_type)}
                {formatSyncType(operation.sync_type)}
              </span>
              <Badge
                variant="outline"
                className={cn(
                  'text-[10px] px-1.5 py-0',
                  operation.status === 'success' && 'border-green-500/30 text-green-400',
                  operation.status === 'failed' && 'border-red-500/30 text-red-400',
                  operation.status === 'skipped' && 'border-muted-foreground/30 text-muted-foreground',
                  operation.status === 'partial' && 'border-yellow-500/30 text-yellow-400'
                )}
              >
                {operation.status}
              </Badge>
            </div>

            {/* Stats line */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
              {operation.status !== 'skipped' && operation.items_processed > 0 && (
                <span>
                  {operation.items_success}/{operation.items_processed} items
                </span>
              )}
              {operation.items_skipped > 0 && (
                <span className="text-yellow-400/80">
                  {operation.items_skipped} skipped
                </span>
              )}
              {operation.duration_ms && (
                <span>{formatDuration(operation.duration_ms)}</span>
              )}
              {operation.message && (
                <span className="truncate max-w-[200px]" title={operation.message}>
                  {operation.message}
                </span>
              )}
            </div>

            {/* Timestamp and trigger info */}
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <span>{operation.triggered_by}</span>
              {operation.user_email && (
                <>
                  <span>·</span>
                  <span>{operation.user_email.split('@')[0]}</span>
                </>
              )}
              <span>·</span>
              <span title={format(new Date(operation.created_at), 'PPpp')}>
                {operation.created_at ? formatPreciseAgo(operation.created_at) : '—'}
              </span>
            </div>
          </div>

          {hasError && (
            <div className="flex-shrink-0 pt-1">
              <ChevronRight
                className={cn(
                  'w-3.5 h-3.5 text-muted-foreground transition-transform',
                  isExpanded && 'rotate-90'
                )}
              />
            </div>
          )}
        </div>

        {/* Expanded error details */}
        {isExpanded && hasError && operation.error_details && (
          <div className="mt-2 ml-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs">
            <div className="text-red-400 font-medium">
              {operation.error_details.name || 'Error'}
            </div>
            <div className="text-red-300/80 mt-1">
              {operation.error_details.message}
            </div>
            {operation.error_details.stack && operation.error_details.stack.length > 0 && (
              <div className="mt-2 text-red-300/60 font-mono text-[10px] overflow-x-auto">
                {operation.error_details.stack.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SolArkSyncHistory({
  defaultExpanded = false,
  pageSize = 20,
  className,
}: SolArkSyncHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [operations, setOperations] = useState<SyncOperation[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Real attempts only by default — paused-sync 'skipped' rows land every
  // 5 minutes and drown the entries an operator actually needs.
  const [includeSkipped, setIncludeSkipped] = useState(false);

  const fetchOperations = useCallback(async (offset = 0, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const response = await authenticatedFetch(
        `/api/v1/solark/sync-history?limit=${pageSize}&offset=${offset}${includeSkipped ? '' : '&excludeSkipped=true'}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to fetch sync history');
      }

      const data = await response.json();

      if (append) {
        setOperations(prev => [...prev, ...data.operations]);
      } else {
        setOperations(data.operations);
      }
      setTotal(data.count);
      setHasMore(data.pagination.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sync history');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [pageSize, includeSkipped]);

  // Fetch when expanded for the first time
  // Refetch whenever the panel opens or the fetcher's inputs change —
  // fetchOperations' identity carries pageSize AND includeSkipped, so toggling
  // the skip filter reloads page one. (The old only-if-empty guard made the
  // filter toggle a no-op once rows were loaded.)
  useEffect(() => {
    if (isExpanded) {
      fetchOperations();
    }
  }, [isExpanded, fetchOperations]);

  const loadMore = () => {
    if (!isLoadingMore && hasMore) {
      fetchOperations(operations.length, true);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={cn('border border-border rounded-lg', className)}
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full flex items-center justify-between p-3 h-auto hover:bg-muted/50"
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Sync History</span>
            {total > 0 && (
              <Badge variant="secondary" className="text-xs">
                {total}
              </Badge>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-3 pb-3">
          {/* Filter: real attempts by default; paused-sync skips on request */}
          <label className="flex items-center gap-2 justify-end pb-2 text-[11px] text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-3 w-3 accent-[#8FB560]"
              checked={includeSkipped}
              onChange={(e) => setIncludeSkipped(e.target.checked)}
              aria-label="Include skipped"
            />
            Include skipped
          </label>

          {/* Loading state */}
          {isLoading && operations.length === 0 && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 py-4">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7"
                onClick={() => fetchOperations()}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Retry
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && operations.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">
              No sync operations recorded yet
            </div>
          )}

          {/* Timeline */}
          {operations.length > 0 && (
            <div className="pt-2">
              {operations.map(op => (
                <SyncOperationEntry
                  key={op.id}
                  operation={op}
                  isExpanded={expandedIds.has(op.id)}
                  onToggle={() => toggleExpanded(op.id)}
                />
              ))}

              {/* Load more button */}
              {hasMore && (
                <div className="pt-2 pl-6">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>Load more</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
